import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  IconButton,
  Button,
  Collapse,
} from "@mui/material";
import {
  Edit,
  ExpandMore,
  ExpandLess,
  InfoOutlined,
} from "@mui/icons-material";
import CustomInput from "./CustomInput";
import NightShiftBadge from "./NightShiftBadge";
import { isNightShift, formatWorkingHours, formatWorkingDays } from "../utils/calendarUtils";

const MemberWorkingHours = React.memo(({ 
  member, 
  editingMemberId, 
  editingMemberSettings, 
  setEditingMemberSettings,
  onUpdateMemberSettings,
  onStartEditing,
  onCancelEditing,
  saving 
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  if (editingMemberId === member.calendar_id) {
    const settings = editingMemberSettings[member.calendar_id] || {};
    const startTime = settings.working_hours_start || member.working_hours_start || '09:00';
    const endTime = settings.working_hours_end || member.working_hours_end || '17:00';
    const isNight = isNightShift(startTime, endTime);

    return (
      <Box sx={{ mt: 1, p: 2, backgroundColor: '#f0f7fa', borderRadius: '6px' }}>
        <Typography variant="caption" fontWeight="500" sx={{ color: '#219297', mb: 1, display: 'block' }}>
          Individual Working Hours
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" sx={{ color: '#666', minWidth: '60px' }}>
                Start:
              </Typography>
              <CustomInput
                type="time"
                value={startTime}
                onChange={(e) => setEditingMemberSettings(prev => ({
                  ...prev,
                  [member.calendar_id]: {
                    ...settings,
                    working_hours_start: e.target.value
                  }
                }))}
                fullWidth={false}
                style={{ flex: 1 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" sx={{ color: '#666', minWidth: '60px' }}>
                End:
              </Typography>
              <CustomInput
                type="time"
                value={endTime}
                onChange={(e) => setEditingMemberSettings(prev => ({
                  ...prev,
                  [member.calendar_id]: {
                    ...settings,
                    working_hours_end: e.target.value
                  }
                }))}
                fullWidth={false}
                style={{ flex: 1 }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="caption" fontWeight="500" sx={{ color: '#219297', mb: 1, display: 'block', mt: 1 }}>
              Working Days
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                <Chip
                  key={day}
                  label={day.slice(0, 3).toUpperCase()}
                  size="small"
                  onClick={() => {
                    const days = [...(settings.working_days || member.working_days || [])];
                    if (days.includes(day)) {
                      setEditingMemberSettings(prev => ({
                        ...prev,
                        [member.calendar_id]: {
                          ...settings,
                          working_days: days.filter(d => d !== day)
                        }
                      }));
                    } else {
                      setEditingMemberSettings(prev => ({
                        ...prev,
                        [member.calendar_id]: {
                          ...settings,
                          working_days: [...days, day]
                        }
                      }));
                    }
                  }}
                  color={(settings.working_days || member.working_days || []).includes(day) ? 'primary' : 'default'}
                  sx={{
                    fontSize: '0.6rem',
                    height: '20px',
                    backgroundColor: (settings.working_days || member.working_days || []).includes(day) ? '#219297' : '#e0e0e0',
                    color: (settings.working_days || member.working_days || []).includes(day) ? '#fff' : '#666',
                  }}
                />
              ))}
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#e1f8f2ff', borderRadius: '6px', border: '1px solid #b3ffd5ff' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" fontWeight="500" sx={{ color: '#007c6cff' }}>
                  <InfoOutlined fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  Night Shift Instructions
                </Typography>
                <IconButton size="small" onClick={() => setShowAdvanced(!showAdvanced)}>
                  {showAdvanced ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Box>
          
          
              <Collapse in={showAdvanced}>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#000000ff', display: 'block', mb: 1 }}>
                    For night shifts like 6 PM to 3 AM or 9 PM to 5 AM:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, '& li': { mb: 0.5 } }}>
                    <li>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Set <strong>Start time</strong> to when shift begins
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Set <strong>End time</strong> to when shift ends the next day
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Example: 18:00 → 03:00 means working from 6 PM to 3 AM next day
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Select the days when the shift starts (Monday-Friday for Mon-Fri night shifts)
                      </Typography>
                    </li>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box display="flex" gap={1} justifyContent="flex-end" mt={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  onCancelEditing();
                  setShowAdvanced(false);
                }}
                sx={{ fontSize: '0.7rem',color: '#000000' }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => onUpdateMemberSettings(member.calendar_id)}
                disabled={saving}
                sx={{ 
                  fontSize: '0.7rem',
                  backgroundColor: '#219297',
                  color:"#ffffff"
                }}
              >
                Save Hours
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }
  
  const isNight = isNightShift(member.working_hours_start, member.working_hours_end);

  return (
    <Box sx={{ mt: 0.5 }}>
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="caption" sx={{ color: '#666' }}>
          Hours: {formatWorkingHours(member.working_hours_start, member.working_hours_end)}
        </Typography>
        {isNight && (
          <NightShiftBadge isNightShift={true} size="small" />
        )}
        <Typography variant="caption" sx={{ color: '#666' }}>
          | Days: {formatWorkingDays(member.working_days)}
        </Typography>
      </Box>
      <Button
        size="small"
        startIcon={<Edit fontSize="small" />}
        onClick={() => onStartEditing(member)}
        sx={{ 
          mt: 0.5,
          fontSize: '0.65rem',
          padding: '2px 6px',
          minWidth: 'auto',
          color:"#219197"
        }}
      >
        Edit Hours
      </Button>
    </Box>
  );
});

export default MemberWorkingHours;